<template>
  <div class="gmp-phone-frame">
    <div class="gmp-phone-screen">
      <!-- Google search bar -->
      <div class="gmp-search-bar" :class="{ 'gmp-search-bar--clickable': !!mapsUri }" @click="openListing">
        <img src="https://www.gstatic.com/images/branding/product/1x/googleg_16dp.png" alt="" class="gmp-google-g" />
        <span class="gmp-search-text">{{ name || 'Rechercher' }}</span>
        <i class="material-icons gmp-search-micro">mic</i>
      </div>

      <!-- Location card -->
      <div class="gmp-card">
        <!-- Name + rating + meta -->
        <div class="gmp-card-header">
          <p class="gmp-name" :class="{ 'gmp-link-title': !!mapsUri }" @click="openListing">{{ name || 'Nom de l\'établissement' }}</p>
          <div class="gmp-meta-row" v-if="rating && parseFloat(rating) > 0">
            <span class="gmp-rating">{{ formatRating(rating) }}</span>
            <span class="gmp-stars">{{ renderStars(rating) }}</span>
            <span class="gmp-review-count">({{ reviewCount || 0 }})</span>
          </div>
          <div class="gmp-cat-row">
            <template v-if="priceRange">
              <span class="gmp-price">{{ priceRange }}</span>
              <span class="gmp-dot">·</span>
            </template>
            <span class="gmp-category">{{ categoryName || 'Établissement' }}</span>
            <span class="gmp-dot">·</span>
            <span :class="isTodayOpen ? 'gmp-open' : 'gmp-closed'">
              {{ isTodayOpen ? $t('dash.presenceManagement.location.profile.preview.open') : $t('dash.presenceManagement.hours.closed') }}
            </span>
          </div>
        </div>

        <!-- Photo strip -->
        <div class="gmp-photos">
          <div
            class="gmp-photo"
            :style="imageUrl ? `background-image: url('${imageUrl}')` : ''"
          ></div>
          <div
            class="gmp-photo"
            :class="{ 'gmp-photo--placeholder': !displayPhoto2 }"
            :style="displayPhoto2 ? `background-image: url('${displayPhoto2}')` : ''"
          ></div>
          <div
            class="gmp-photo"
            :class="{ 'gmp-photo--placeholder': !displayPhoto3 }"
            :style="displayPhoto3 ? `background-image: url('${displayPhoto3}')` : ''"
          ></div>
          
          <!-- Hover Overlay to add photos -->
          <div class="gmp-photos-overlay" @click.stop="$emit('add-photo')">
            <i class="material-icons">add_a_photo</i>
            <span>{{ $t('dash.presenceManagement.location.media.addPhoto') }}</span>
          </div>
        </div>

        <!-- Action buttons (Itinéraire, Site Web, Avis, Partager) -->
        <div class="gmp-actions">
          <div
            class="gmp-action"
            :class="{ 'gmp-action--disabled': !mapsUri && !address }"
            @click="openDirections"
            :title="$t('dash.presenceManagement.location.profile.preview.directions')"
          >
            <i class="material-icons gmp-action-icon">directions</i>
            <span class="gmp-action-label">{{ $t('dash.presenceManagement.location.profile.preview.directions') }}</span>
          </div>

          <div
            class="gmp-action"
            :class="{ 'gmp-action--disabled': !website }"
            @click="openWebsite"
            :title="$t('dash.presenceManagement.location.profile.preview.website')"
          >
            <i class="material-icons gmp-action-icon">language</i>
            <span class="gmp-action-label">{{ $t('dash.presenceManagement.location.profile.preview.website') }}</span>
          </div>

          <div
            class="gmp-action"
            @click="$emit('switch-tab', 'reviews')"
            :title="$t('dash.presenceManagement.location.profile.preview.reviews')"
          >
            <i class="material-icons gmp-action-icon">rate_review</i>
            <span class="gmp-action-label">{{ $t('dash.presenceManagement.location.profile.preview.reviews') }}</span>
          </div>

          <div
            class="gmp-action"
            @click="shareListing"
            :title="$t('dash.presenceManagement.mockup.share')"
          >
            <i class="material-icons gmp-action-icon">share</i>
            <span class="gmp-action-label">{{ $t('dash.presenceManagement.mockup.share') }}</span>
          </div>
        </div>

        <!-- Info rows -->
        <div class="gmp-info-rows">
          <div class="gmp-info-row" v-if="address">
            <i class="material-icons gmp-info-icon">location_on</i>
            <span class="gmp-info-text">{{ address }}</span>
          </div>

          <!-- Hours Row with Dropdown toggle -->
          <div class="gmp-info-row gmp-info-row--clickable" @click="showHoursList = !showHoursList">
            <i class="material-icons gmp-info-icon">schedule</i>
            <span class="gmp-info-text" :class="isTodayOpen ? 'gmp-open' : 'gmp-closed'">
              {{ formattedHoursToday }}
            </span>
            <i class="material-icons gmp-info-expand">{{ showHoursList ? 'expand_less' : 'expand_more' }}</i>
          </div>

          <!-- Expanded Weekly Hours List (Centered & Full Width, Uniform Grey, Bold Today) -->
          <div class="gmp-hours-dropdown" v-if="showHoursList">
            <div
              v-for="(h, idx) in weeklyHoursList"
              :key="idx"
              class="gmp-hours-item"
              :class="{ 'gmp-hours-item--today': h.isToday }"
            >
              <span class="gmp-hours-day">{{ h.dayLabel }}</span>
              <span class="gmp-hours-time">
                {{ h.hoursText }}
              </span>
            </div>
          </div>

          <div class="gmp-info-row gmp-info-row--clickable" v-if="website" @click="openWebsite">
            <i class="material-icons gmp-info-icon">language</i>
            <span class="gmp-info-text gmp-link">{{ cleanWebsite }}</span>
          </div>

          <div class="gmp-info-row gmp-info-row--clickable" v-if="phone" @click="callPhone">
            <i class="material-icons gmp-info-icon">phone</i>
            <span class="gmp-info-text gmp-link">{{ phone }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'google-mockup-preview',

  props: {
    name: { type: String, default: '' },
    address: { type: String, default: '' },
    phone: { type: String, default: '' },
    website: { type: String, default: '' },
    categoryName: { type: String, default: '' },
    imageUrl: { type: String, default: '' },
    coverPhoto: { type: String, default: '' },
    additionalPhotos: { type: Array, default: () => [] },
    regularHours: { type: Object, default: null },
    rating: { type: [Number, String], default: null },
    reviewCount: { type: [Number, String], default: null },
    mapsUri: { type: String, default: '' },
    priceRange: { type: String, default: null }
  },

  data() {
    return {
      showHoursList: false
    }
  },

  computed: {
    displayPhoto2() {
      return this.coverPhoto || (this.additionalPhotos && this.additionalPhotos[0]) || ''
    },

    displayPhoto3() {
      if (this.coverPhoto) {
        return (this.additionalPhotos && this.additionalPhotos[0]) || ''
      }
      return (this.additionalPhotos && this.additionalPhotos[1]) || ''
    },

    cleanWebsite() {
      if (!this.website) return ''
      return this.website.replace(/^https?:\/\//i, '').replace(/\/$/, '')
    },

    isTodayOpen() {
      if (!this.regularHours || !Array.isArray(this.regularHours.periods)) {
        return false
      }
      const daysMap = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY']
      const todayDay = daysMap[new Date().getDay()]
      return this.regularHours.periods.some(p => p.openDay === todayDay)
    },

    formattedHoursToday() {
      if (!this.regularHours || !Array.isArray(this.regularHours.periods)) {
        return 'Horaires non renseignés'
      }

      const daysMap = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY']
      const todayDay = daysMap[new Date().getDay()]

      const periods = this.regularHours.periods.filter(p => p.openDay === todayDay)
      if (periods.length === 0) {
        return 'Fermé aujourd\'hui'
      }

      const formattedPeriods = periods.map(p => {
        if (p.openTime === '00:00' && (p.closeTime === '00:00' || p.closeTime === '24:00')) {
          return '24h/24'
        }
        return `${p.openTime} - ${p.closeTime}`
      })

      return `Ouvert aujourd'hui : ${formattedPeriods.join(', ')}`
    },

    weeklyHoursList() {
      if (!this.regularHours || !Array.isArray(this.regularHours.periods)) {
        return []
      }

      const daysOrder = [
        { key: 'MONDAY', label: 'Lundi' },
        { key: 'TUESDAY', label: 'Mardi' },
        { key: 'WEDNESDAY', label: 'Mercredi' },
        { key: 'THURSDAY', label: 'Jeudi' },
        { key: 'FRIDAY', label: 'Vendredi' },
        { key: 'SATURDAY', label: 'Samedi' },
        { key: 'SUNDAY', label: 'Dimanche' }
      ]

      const daysMapIndex = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY']
      const todayKey = daysMapIndex[new Date().getDay()]

      return daysOrder.map(d => {
        const periods = this.regularHours.periods.filter(p => p.openDay === d.key)
        const isOpen = periods.length > 0
        let hoursText = 'Fermé'
        if (isOpen) {
          hoursText = periods.map(p => {
            if (p.openTime === '00:00' && (p.closeTime === '00:00' || p.closeTime === '24:00')) return '24h/24'
            return `${p.openTime} - ${p.closeTime}`
          }).join(', ')
        }

        return {
          dayLabel: d.label,
          hoursText,
          isOpen,
          isToday: d.key === todayKey
        }
      })
    }
  },

  methods: {
    formatRating(val) {
      const num = parseFloat(val)
      return isNaN(num) ? '4.5' : num.toFixed(1).replace('.', ',')
    },

    renderStars(val) {
      const num = parseFloat(val)
      if (isNaN(num)) return '★★★★½'
      const fullStars = Math.floor(num)
      const halfStar = num % 1 >= 0.5 ? '½' : ''
      const emptyStars = Math.max(0, 5 - fullStars - (halfStar ? 1 : 0))
      return '★'.repeat(fullStars) + halfStar + '☆'.repeat(emptyStars)
    },

    openListing() {
      if (this.mapsUri) {
        window.open(this.mapsUri, '_blank')
      }
    },

    openDirections() {
      if (this.mapsUri) {
        window.open(this.mapsUri, '_blank')
      } else if (this.address) {
        const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(this.address)}`
        window.open(url, '_blank')
      }
    },

    openWebsite() {
      if (this.website) {
        const target = this.website.startsWith('http') ? this.website : `https://${this.website}`
        window.open(target, '_blank')
      }
    },

    callPhone() {
      if (this.phone) {
        window.location.href = `tel:${this.phone}`
      }
    },

    shareListing() {
      const shareUrl = this.mapsUri || this.website || window.location.href
      if (navigator.share) {
        navigator.share({ title: this.name, url: shareUrl }).catch(() => {})
      } else {
        window.open(shareUrl, '_blank')
      }
    }
  }
}
</script>

<style scoped>
.gmp-phone-frame {
  background: #e8e8e8;
  border-radius: 36px;
  padding: 14px 10px;
  width: 260px;
  flex-shrink: 0;
  box-shadow: 0 8px 32px rgba(0,0,0,0.08), inset 0 0 0 2px #dadada;
  margin: 0 auto;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
}

.gmp-phone-screen {
  background: #fff;
  border-radius: 24px;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
}

/* ─── Search bar ─── */
.gmp-search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #e8eaed;
}

.gmp-search-bar--clickable {
  cursor: pointer;
}

.gmp-google-g { width: 14px; height: 14px; flex-shrink: 0; }

.gmp-search-text {
  flex: 1;
  font-size: 11px;
  color: #202124;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gmp-search-micro { font-size: 16px; color: #4285f4; flex-shrink: 0; }

/* ─── Card ─── */
.gmp-card { padding: 12px; display: flex; flex-direction: column; gap: 10px; box-sizing: border-box; }

.gmp-name {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #202124;
  line-height: 1.3;
}

.gmp-link-title {
  cursor: pointer;
  transition: color 0.15s;
}

.gmp-link-title:hover {
  color: #1a0dab;
}

.gmp-meta-row {
  display: flex;
  align-items: center;
  gap: 3px;
  margin-top: 2px;
}

.gmp-rating { font-size: 11px; font-weight: 700; color: #e37400; }
.gmp-stars { font-size: 10px; color: #e37400; letter-spacing: -1px; }
.gmp-review-count { font-size: 10px; color: #70757a; }

.gmp-cat-row { display: flex; align-items: center; gap: 4px; margin-top: 2px; }
.gmp-price { font-size: 11px; color: #70757a; }
.gmp-dot { font-size: 11px; color: #70757a; }
.gmp-category { font-size: 11px; color: #70757a; }
.gmp-open { font-size: 11px; color: #188038 !important; font-weight: 600; }
.gmp-closed { font-size: 11px; color: #d93025 !important; font-weight: 600; }

/* ─── Photos ─── */
.gmp-photos {
  position: relative;
  display: flex;
  gap: 3px;
  height: 70px;
  border-radius: 8px;
  overflow: hidden;
}

.gmp-photos-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(106, 31, 76, 0.6);
  backdrop-filter: blur(2px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #fff;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  cursor: pointer;
}

.gmp-photos:hover .gmp-photos-overlay {
  opacity: 1;
}

.gmp-photos-overlay i {
  font-size: 20px;
}

.gmp-photos-overlay span {
  font-size: 10px;
  font-weight: 600;
}

.gmp-photo {
  flex: 1;
  background-color: #e8d5f0;
  background-size: cover;
  background-position: center;
}

.gmp-photo--placeholder { background-color: #f0e6f8; }

/* ─── Actions ─── */
.gmp-actions {
  display: flex;
  gap: 4px;
  justify-content: space-around;
  border-top: 1px solid #f1f3f4;
  border-bottom: 1px solid #f1f3f4;
  padding: 8px 0;
}

.gmp-action {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  transition: background-color 0.15s;
}

.gmp-action:hover {
  background-color: #e8f0fe;
}

.gmp-action--disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.gmp-action--disabled:hover {
  background-color: transparent;
}

.gmp-action-icon { font-size: 16px; color: #1a73e8; }

.gmp-action-label {
  font-size: 9px;
  color: #1a73e8;
  text-align: center;
  font-weight: 500;
}

/* ─── Info rows ─── */
.gmp-info-rows { display: flex; flex-direction: column; gap: 8px; }

.gmp-info-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.gmp-info-row--clickable {
  cursor: pointer;
}

.gmp-info-icon { font-size: 14px; color: #70757a; flex-shrink: 0; margin-top: 1px; }

.gmp-info-text {
  font-size: 11px;
  color: #3c4043;
  flex: 1;
  line-height: 1.4;
  word-break: break-word;
}

.gmp-info-expand { font-size: 14px; color: #70757a; flex-shrink: 0; }
.gmp-link { color: #1a0dab; }
.gmp-link:hover { text-decoration: underline; }

/* ─── Hours Dropdown (Full Width, Centered, Grey Text, Bold Today) ─── */
.gmp-hours-dropdown {
  background: #f8f9fa;
  border: 1px solid #e8eaed;
  border-radius: 6px;
  padding: 8px 12px;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 2px;
}

.gmp-hours-item {
  display: flex;
  justify-content: space-between;
  font-size: 10.5px;
  color: #5f6368;
}

.gmp-hours-item--today {
  font-weight: 700;
  color: #202124;
}

.gmp-hours-day { min-width: 65px; }

.gmp-hours-time { text-align: right; }
</style>
